import {
  ChangeDetectionStrategy,
  Component,
  computed,
  contentChild,
  ElementRef,
  forwardRef,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MaskitoDirective } from '@maskito/angular';
import { maskitoTransform, maskitoUpdateElement } from '@maskito/core';

import { CadsIconViewBitComponent } from '../../bits/cads-icon-view-bit/cads-icon-view-bit.component';
import type { CadsIconViewBitConfig } from '../../bits/cads-icon-view-bit/cads-icon-view-bit.config';
import { CadsFieldComponent } from '../cads-field/cads-field.component';
import { CadsFieldCaptionSlotDirective } from '../cads-field/cads-field-caption-slot.directive';
import type { CadsFieldConfig } from '../cads-field/cads-field.config';
import { nextCadsFormKitId } from '../internal/cads-form-kit-ids';
import { cadsInputMaskitoOptions } from '../internal/cads-input-maskito-presets';

import type { CadsInputConfig } from './cads-input.config';

/**
 * Reactive-forms native `<input>` for `text | email | password | tel | url | number | date`. Implements
 * {@link ControlValueAccessor} so consumers bind via `formControlName="x"` (or `[formControl]`). Visual chrome (label,
 * optional leading/trailing icons, helper, error caption) comes from the embedded {@link CadsFieldComponent}.
 *
 * Value shape: always a `string` matching the native input's `value` (date inputs use ISO `yyyy-MM-dd`; number inputs
 * surface the raw `string` so consumers can apply their own coercion / validators). With {@link CadsInputConfig.mask},
 * Maskito owns the displayed string; the CVA still stores the **masked** text (see preset docs).
 */
@Component({
    selector: 'cads-input',
    imports: [CadsFieldComponent, CadsIconViewBitComponent, MaskitoDirective],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => CadsInputComponent),
            multi: true,
        },
    ],
    templateUrl: './cads-input.component.html',
    styleUrl: './cads-input.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        class: 'cads-input-host',
    }
})
export class CadsInputComponent implements ControlValueAccessor {
  readonly config = input.required<CadsInputConfig>();
  /** Emitted when the trailing control is activated (e.g. clear). */
  readonly trailingTapped = output<void>();
  /** Emitted when Enter is pressed, the field is not disabled, and `submitOnEnter` is **false**. */
  readonly submitted = output<void>();

  private readonly _inputRef = viewChild<ElementRef<HTMLInputElement>>('inputEl');

  private readonly _fallbackInputId = nextCadsFormKitId('cads-input');

  protected readonly _value = signal<string>('');
  protected readonly _focused = signal(false);
  protected readonly _controlDisabled = signal(false);

  protected readonly _inputId = computed(() => {
    const fromConfig = this.config().inputId?.trim();
    return fromConfig || this._fallbackInputId;
  });

  protected readonly _disabled = computed(() => this._controlDisabled() || this.config().disabled === true);

  protected readonly _type = computed(() => this.config().type ?? 'text');

  /** Maskito options for the active {@link CadsInputConfig.mask}, or `null` when masking is disabled. */
  protected readonly _maskitoOptions = computed(() => cadsInputMaskitoOptions(this.config().mask));

  protected readonly _fieldConfig = computed((): CadsFieldConfig => ({
    label: this.config().label,
    controlId: this._inputId(),
    helperText: this.config().helperText,
    errorMessage: this.config().errorMessage,
    required: this.config().required === true,
  }));

  protected readonly _hasError = computed(() => (this.config().errorMessage?.trim() ?? '').length > 0);

  private readonly _captionSlot = contentChild(CadsFieldCaptionSlotDirective);

  protected readonly _hasProjectedCaption = computed(() => this._captionSlot() !== undefined);

  protected readonly _captionId = computed(() => `${this._inputId()}-caption`);

  protected readonly _showCaption = computed(() => {
    if (this._hasError()) {
      return true;
    }
    if (this._hasProjectedCaption()) {
      return true;
    }
    return (this.config().helperText?.trim() ?? '').length > 0;
  });

  protected readonly _leadingIconConfig = computed((): CadsIconViewBitConfig | null => {
    const icon = this.config().leadingIcon?.trim();
    if (!icon) {
      return null;
    }
    const color = this._hasError()
      ? 'var(--cads-color-red)'
      : this._focused()
        ? 'var(--cads-color-blue)'
        : 'var(--cads-color-mid-gray)';
    return this._spriteIconConfig(icon, color);
  });

  protected readonly _trailingIconConfig = computed((): CadsIconViewBitConfig | null => {
    const icon = this.config().trailingIcon?.trim();
    if (!icon) {
      return null;
    }
    return this._spriteIconConfig(icon);
  });

  protected readonly _showTrailing = computed(() => {
    if (!this._trailingIconConfig()) {
      return false;
    }
    return this._value().trim().length > 0;
  });

  private _onChange: (value: string) => void = () => undefined;
  private _onTouched: () => void = () => undefined;

  writeValue(value: string | number | null | undefined): void {
    const s = value === null || value === undefined ? '' : String(value);
    this._value.set(s);
    if (this._maskitoOptions()) {
      // `MaskitoDirective` initializes asynchronously; defer past its microtask queue so
      // `maskitoUpdateElement` runs against an attached Maskito instance.
      setTimeout(() => this._patchMaskedDom(), 0);
    }
  }

  /** Pushes programmatic values into a Maskito-managed input (custom CVA; no `DefaultValueAccessor` hook). */
  private _patchMaskedDom(): void {
    const el = this._inputRef()?.nativeElement;
    const opts = this._maskitoOptions();
    if (!el || !opts) {
      return;
    }
    maskitoUpdateElement(el, maskitoTransform(this._value(), opts));
  }

  registerOnChange(fn: (value: string) => void): void {
    this._onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this._onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this._controlDisabled.set(isDisabled);
  }

  protected _onInput(event: Event): void {
    const next = (event.target as HTMLInputElement).value;
    this._value.set(next);
    this._onChange(next);
  }

  protected _onFocus(): void {
    this._focused.set(true);
  }

  protected _onBlur(): void {
    this._focused.set(false);
    this._onTouched();
  }

  protected _onTrailingClick(): void {
    if (this._disabled()) {
      return;
    }
    this.trailingTapped.emit();
  }

  protected _onKeydownEnter(event: Event): void {
    if (this._disabled()) {
      return;
    }
    if (this.config().submitOnEnter === false) {
      event.preventDefault();
      this.submitted.emit();
    }
  }

  /**
   * `color` drives {@link CadsIconViewBitConfig} glyph tokens. Leading uses red (error) / blue (focus) / mid-gray;
   * omit `iconColor` so the sprite fill follows `color` (projected leading cannot rely on parent `color` due to view
   * encapsulation).
   */
  private _spriteIconConfig(icon: string, color = 'var(--cads-color-mid-gray)'): CadsIconViewBitConfig {
    return {
      icon,
      shape: 'icon',
      size: 'medium',
      collapsed: true,
      iconSizePx: 20,
      color,
    };
  }
}
